<template>
  <v-card
    class="address-detail-card"
    :class="{
      'address-default': info.user_address_default,
      'address-editing': edit,
    }"
    elevation="2"
  >
    <!-- View Mode -->
    <div v-if="!edit" class="address-view">
      <!-- Default Badge -->
      <div v-if="info.user_address_default" class="default-badge">
        <v-chip color="#fddf00" small>
          <v-icon class="mr-2" size="12">home</v-icon>
          Default
        </v-chip>
      </div>

      <!-- Address Content -->
      <div class="address-content">
        <div class="address-icon">
          <v-icon color="#fddf00" size="24">place</v-icon>
        </div>

        <div class="address-info">
          <h4 class="address-name">{{ info.user_address_name }}</h4>
          <div class="address-details">
            <p class="address-text">{{ info.user_address_text }}</p>
            <div class="address-location">
              <span class="location-item">{{
                info.user_address_sub_district
              }}</span>
              <span class="separator">•</span>
              <span class="location-item">{{
                info.user_address_district
              }}</span>
              <span class="separator">•</span>
              <span class="location-item">{{
                info.user_address_province
              }}</span>
              <span class="separator">•</span>
              <span class="location-item">{{ info.user_address_zipcode }}</span>
            </div>
            <div v-if="info.user_address_note" class="address-note">
              <v-icon size="14" color="grey">note</v-icon>
              <span>{{ info.user_address_note }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="address-actions">
        <v-btn
          v-if="!info.user_address_default"
          @click="setAsDefault()"
          :disabled="isEditCustomer"
          color="#fddf00"
          small
          outlined
          class="action-btn"
        >
          <v-icon size="16" class="mr-2">home</v-icon>
          Set Default
        </v-btn>

        <v-btn
          @click="toEdit()"
          :disabled="isEditCustomer"
          color="#fddf00"
          small
          outlined
          class="action-btn"
        >
          <v-icon class="mr-2" size="16">edit</v-icon>
          Edit
        </v-btn>

        <v-btn
          @click="toDelete()"
          :disabled="isEditCustomer"
          color="error"
          small
          outlined
          class="action-btn"
        >
          <v-icon class="mr-2" size="16">delete</v-icon>
          Delete
        </v-btn>
      </div>
    </div>

    <!-- Edit Mode -->
    <div v-else class="address-edit">
      <div class="edit-header">
        <h4 class="edit-title">
          <v-icon color="#fddf00" class="mr-2">edit_location</v-icon>
          Edit Address
        </h4>
      </div>

      <v-form ref="formEdit" v-model="valid" lazy-validation class="edit-form">
        <!-- Address Name -->
        <v-text-field
          v-model="info.user_address_name"
          label="Address Name"
          outlined
          dense
          color="#fddf00"
          :rules="[validation.required]"
          class="form-field"
        >
          <template v-slot:prepend-inner>
            <v-icon color="#fddf00" size="20">label</v-icon>
          </template>
        </v-text-field>

        <!-- Address Text -->
        <v-textarea
          v-model="info.user_address_text"
          label="Address"
          outlined
          dense
          rows="2"
          color="#fddf00"
          :rules="[validation.required]"
          class="form-field"
        >
          <template v-slot:prepend-inner>
            <v-icon color="#fddf00" size="20">home</v-icon>
          </template>
        </v-textarea>

        <!-- Location Fields -->
        <div class="location-grid">
          <v-select
            :items="provinceItems"
            v-model="info.user_address_province"
            @change="onChangeProvince"
            item-value="province_name_th"
            item-text="province_name_th"
            label="Province"
            outlined
            dense
            color="#fddf00"
            :rules="[validation.required]"
            class="form-field"
          ></v-select>

          <v-select
            :items="districtItems"
            v-model="info.user_address_district"
            @change="onChangeDistrict"
            item-text="district_name_th"
            item-value="district_name_th"
            label="District"
            outlined
            dense
            color="#fddf00"
            :rules="[validation.required]"
            class="form-field"
          ></v-select>

          <v-select
            :items="subDistrictItems"
            v-model="info.user_address_sub_district"
            item-text="sub_district_name_th"
            label="Sub District"
            outlined
            dense
            color="#fddf00"
            :rules="[validation.required]"
            class="form-field"
          ></v-select>

          <v-select
            :items="postcodeItems"
            v-model="info.user_address_zipcode"
            item-text="zipcode_code"
            label="Postal Code"
            outlined
            dense
            color="#fddf00"
            :rules="[validation.required]"
            class="form-field"
          ></v-select>
        </div>

        <!-- Coordinates -->
        <div class="coordinates-grid">
          <v-text-field
            v-model="info.user_address_lat"
            label="Latitude"
            outlined
            dense
            color="#fddf00"
            :rules="[rules.required, rules.onlyDouble]"
            class="form-field"
          ></v-text-field>

          <v-text-field
            v-model="info.user_address_long"
            label="Longitude"
            outlined
            dense
            color="#fddf00"
            :rules="[rules.required, rules.onlyDouble]"
            class="form-field"
          ></v-text-field>
        </div>

        <!-- Note -->
        <v-textarea
          v-model="info.user_address_note"
          label="Address Note"
          outlined
          dense
          rows="2"
          color="#fddf00"
          class="form-field"
          hint="Optional additional information"
        >
          <template v-slot:prepend-inner>
            <v-icon color="#fddf00" size="20">note</v-icon>
          </template>
        </v-textarea>

        <!-- Map Update -->
        <div class="map-section">
          <v-btn
            @click="change_location++"
            color="#fddf00"
            outlined
            small
            class="map-update-btn"
          >
            <v-icon class="mr-2" size="16">refresh</v-icon>
            Update Map
          </v-btn>

          <div class="map-container">
            <GoogleMap
              :latitude="parseFloat(info.user_address_lat)"
              :longitude="parseFloat(info.user_address_long)"
              :change="change_location"
              :height="200"
              :zoom="15"
              :mapType="'all'"
              :draggable="true"
              @draged="getDragLocation($event)"
            />
          </div>
        </div>

        <!-- Edit Actions -->
        <div class="edit-actions">
          <v-btn
            @click="cancelEdit()"
            color="grey"
            outlined
            small
            class="action-btn"
            style="color: white"
          >
            <v-icon class="mr-2" size="16" color="white">cancel</v-icon>
            Cancel
          </v-btn>

          <v-btn
            @click="save()"
            :disabled="isEditCustomer"
            color="#fddf00"
            small
            class="action-btn"
          >
            <v-icon class="mr-2" size="16">save</v-icon>
            Save Changes
          </v-btn>
        </div>
      </v-form>
    </div>
  </v-card>
</template>

<script>
import { mapState, mapGetters } from "vuex";

export default {
  props: ["info", "selectedCustomer", "provinceItems"],
  data() {
    return {
      rules: {
        onlyDouble: (value) => {
          const pattern = /^-?([0-9]?[0-9]?[0-9]?[0-9]|[0-9]0)\.{1}\d{1,100}$/;
          return pattern.test(value) || "Invalid coordinate format";
        },
        required: (value) => !!value || "This field is required",
      },
      change_location: 0,
      districtItems: [],
      postcodeItems: [],
      provinceItems: [],
      subDistrictItems: [],
      originalData: null,
      edit: false,
      valid: true,
      validation: {
        required: (value) => !!value || "This field is required",
      },
    };
  },
  computed: {
    ...mapState({
      customerData: (state) => state.fetchCustomerBack.customerData,
    }),
    ...mapGetters(["isEditCustomer"]),
  },
  methods: {
    getDragLocation(val) {
      this.info.user_address_lat = val.lat;
      this.info.user_address_long = val.lng;
    },

    async setAsDefault() {
      let index = this.selectedCustomer.user_address.indexOf(this.info);
      for (let key in this.selectedCustomer.user_address) {
        this.selectedCustomer.user_address[key].user_address_default = false;
      }
      this.selectedCustomer.user_address[index].user_address_default = true;
      for (let key in this.selectedCustomer.user_address) {
        if (
          this.selectedCustomer.user_address[key].user_address_default == true
        ) {
          let sendData = {
            user_address_default: 1,
            user_address_id:
              this.selectedCustomer.user_address[key].user_address_id,
            user_address_user: this.selectedCustomer.user_id,
          };
          await this.$store.dispatch(
            "fetchCustomerBack/setAsDefault",
            sendData
          );
        } else if (
          this.selectedCustomer.user_address[key].user_address_default == false
        ) {
          let sendData = {
            user_address_default: 0,
            user_address_id:
              this.selectedCustomer.user_address[key].user_address_id,
            user_address_user: this.selectedCustomer.user_id,
          };
          await this.$store.dispatch(
            "fetchCustomerBack/setAsDefault",
            sendData
          );
        }
      }
    },
    toDelete() {
      let index_customer = this.customerData.indexOf(this.selectedCustomer);
      let send_data = {
        index_customer: index_customer,
        address_info: this.info,
      };

      if (confirm("Are you sure you want to delete this address?")) {
        this.$store.dispatch(
          "fetchCustomerBack/deleteCustomerAddress",
          send_data
        );
      }
    },
    async onChangeDistrict() {
      let district_id = null;
      for (let key in this.districtItems) {
        if (
          this.districtItems[key].district_name_th ==
          this.info.user_address_district
        ) {
          district_id = this.districtItems[key].district_id;
        }
      }
      this.info.user_address_sub_district = null;
      this.info.user_address_zipcode = null;
      this.subDistrictItems = [];
      this.postcodeItems = [];
      this.subDistrictItems = await this.$store.dispatch(
        "fetchCustomerBack/getSubDistrictEdit",
        district_id
      );

      this.postcodeItems = await this.$store.dispatch(
        "fetchCustomerBack/getZipCodeEdit",
        district_id
      );
    },
    async initDistrict() {
      let district_id = null;
      for (let key in this.districtItems) {
        if (
          this.districtItems[key].district_name_th ==
          this.info.user_address_district
        ) {
          district_id = this.districtItems[key].district_id;
        }
      }
      this.subDistrictItems = [];
      this.postcodeItems = [];
      this.subDistrictItems = await this.$store.dispatch(
        "fetchCustomerBack/getSubDistrictEdit",
        district_id
      );

      this.postcodeItems = await this.$store.dispatch(
        "fetchCustomerBack/getZipCodeEdit",
        district_id
      );
    },
    async onChangeProvince() {
      let province_id = null;
      for (let key in this.provinceItems) {
        if (
          this.provinceItems[key].province_name_th ==
          this.info.user_address_province
        ) {
          province_id = this.provinceItems[key].province_id;
        }
      }
      this.info.user_address_district = null;
      this.info.user_address_sub_district = null;
      this.info.user_address_zipcode = null;
      this.subDistrictItems = [];
      this.postcodeItems = [];
      this.districtItems = [];
      this.districtItems = await this.$store.dispatch(
        "fetchCustomerBack/getDistrictEdit",
        province_id
      );
    },
    async initProvince() {
      let province_id = null;
      for (let key in this.provinceItems) {
        if (
          this.provinceItems[key].province_name_th ==
          this.info.user_address_province
        ) {
          province_id = this.provinceItems[key].province_id;
        }
      }
      this.subDistrictItems = [];
      this.postcodeItems = [];
      this.districtItems = [];
      this.districtItems = await this.$store.dispatch(
        "fetchCustomerBack/getDistrictEdit",
        province_id
      );
    },
    cancelEdit() {
      Object.assign(this.info, this.originalData);
      this.edit = false;
      this.originalData = null;
    },
    async toEdit() {
      this.originalData = Object.assign({}, this.info);
      this.edit = true;
      await this.initProvince();
      this.initDistrict();
    },
    async save() {
      if (this.$refs.formEdit.validate()) {
        let user_address_default = null;
        if (this.info.user_address_default == true) {
          user_address_default = 1;
        } else {
          user_address_default = 0;
        }
        let user_address_sub_district = null;
        for (let key in this.subDistrictItems) {
          if (
            this.subDistrictItems[key].sub_district_name_th ==
            this.info.user_address_sub_district
          ) {
            user_address_sub_district =
              this.subDistrictItems[key].sub_district_id;
          }
        }

        let user_address_district = null;
        for (let key in this.districtItems) {
          if (
            this.districtItems[key].district_name_th ==
            this.info.user_address_district
          ) {
            user_address_district = this.districtItems[key].district_id;
          }
        }

        let user_address_province = null;
        for (let key in this.provinceItems) {
          if (
            this.provinceItems[key].province_name_th ==
            this.info.user_address_province
          ) {
            user_address_province = this.provinceItems[key].province_id;
          }
        }

        let user_address_zipcode = null;
        for (let key in this.postcodeItems) {
          if (
            this.postcodeItems[key].zipcode_code ==
            this.info.user_address_zipcode
          ) {
            user_address_zipcode = this.postcodeItems[key].zipcode_id;
          }
        }

        const sendInfo = {
          user_address_id: this.info.user_address_id,
          user_address_default: user_address_default,
          user_address_note:
            this.info.user_address_note == ""
              ? null
              : this.info.user_address_note,
          user_address_text: this.info.user_address_text,
          user_address_lat: parseFloat(this.info.user_address_lat),
          user_address_long: parseFloat(this.info.user_address_long),
          user_address_user: this.selectedCustomer.user_id,
          user_address_sub_district: user_address_sub_district,
          user_address_district: user_address_district,
          user_address_province: user_address_province,
          user_address_zipcode: user_address_zipcode,
          user_address_name: this.info.user_address_name,
        };
        this.edit = false;
        let isSuccess = await this.$store.dispatch(
          "fetchCustomerBack/editCustomerAddress",
          sendInfo
        );
        if (isSuccess == "error") {
          Object.assign(this.info, this.originalData);
        }
        this.clear();
      }
    },
    clear() {
      this.$refs.form.reset();
    },
  },
};
</script>

<style scoped>
.address-detail-card {
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid #e9ecef;
}

.address-detail-card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.address-default {
  border-color: #fddf00;
  box-shadow: 0 2px 12px rgba(253, 223, 0, 0.2);
}

.address-editing {
  border-color: #2196f3;
  box-shadow: 0 4px 20px rgba(33, 150, 243, 0.2);
}

.address-view {
  position: relative;
  padding: 1.5rem;
}

.default-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
}

.address-content {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.address-icon {
  background: rgba(253, 223, 0, 0.1);
  border-radius: 50%;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  height: fit-content;
}

.address-info {
  flex: 1;
}

.address-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 0.5rem 0;
}

.address-text {
  font-size: 0.95rem;
  color: #4b5563;
  margin: 0 0 0.5rem 0;
  line-height: 1.4;
}

.address-location {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

.location-item {
  font-size: 0.85rem;
  color: #6b7280;
}

.separator {
  color: #d1d5db;
  font-size: 0.8rem;
}

.address-note {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #6b7280;
  background: #f8f9fa;
  padding: 0.5rem;
  border-radius: 6px;
  margin-top: 0.5rem;
}

.address-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.action-btn {
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
}

.address-edit {
  padding: 1.5rem;
}

.edit-header {
  margin-bottom: 1.5rem;
}

.edit-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.edit-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-field {
  margin-bottom: 0.5rem;
}

.location-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.coordinates-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.map-section {
  margin: 1rem 0;
}

.map-update-btn {
  margin-bottom: 1rem;
}

.map-container {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e9ecef;
}

.edit-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .address-content {
    flex-direction: column;
    gap: 0.75rem;
  }

  .default-badge {
    position: static;
    margin-bottom: 1rem;
  }

  .address-actions {
    flex-direction: column;
  }

  .action-btn {
    width: 100%;
    justify-content: center;
  }

  .location-grid,
  .coordinates-grid {
    grid-template-columns: 1fr;
  }

  .edit-actions {
    flex-direction: column;
  }
}
</style>
